import { type FC } from "react";
import { PeriodYearList } from "../../patterns/period-selector";
import { SideDrawer, type SideDrawerControlProps } from "../../patterns/side-drawer";
import { SegmentedControls, Text } from "../../primitives";
import { WipAnalysisPeriodSelectorLabel } from "./WipAnalysisPeriodSelectorLabel";
import { useWipAnalysisPeriodSelector } from "./WipAnalysisPeriodSelectorProvider";
import { type WipAnalysisPeriodSelectorItem } from "./wipAnalysisPeriodSelection";

export const WIP_ANALYSIS_PERIOD_SELECTOR_ID = "wip-analysis-period-selector";

const periodSelectorIds = (suffix: string) => {
	const id = `${WIP_ANALYSIS_PERIOD_SELECTOR_ID}-${suffix}`;
	return { "data-testid": id, trackingId: id };
};

export const WipAnalysisPeriodSelectorDrawer: FC<SideDrawerControlProps> = ({
	id,
	isOpen,
	onClose,
	testId = `${WIP_ANALYSIS_PERIOD_SELECTOR_ID}-drawer`,
	trackingId,
}) => (
	<SideDrawer
		id={id}
		isOpen={isOpen}
		onClose={onClose}
		title="Periods"
		closeLabel="Close period selector"
		testId={testId}
		trackingId={trackingId}
		className="min-h-0"
	>
		<WipAnalysisPeriodSelectorDrawerPanel />
	</SideDrawer>
);

const getItemKey = (item: WipAnalysisPeriodSelectorItem) => item.extractionId;

const renderLabel = (item: WipAnalysisPeriodSelectorItem) => (
	<WipAnalysisPeriodSelectorLabel item={item} />
);

const WipAnalysisPeriodSelectorDrawerPanel: FC = () => {
	const { periodYearGroups, isItemSelected, toggleItem, periodOrder, setPeriodOrder } =
		useWipAnalysisPeriodSelector();

	return (
		<div className="flex min-h-0 flex-1 flex-col">
			<div className="border-b border-bc-subtle px-4 py-3">
				<div className="flex items-center justify-between gap-3">
					<Text type="caption" color="secondary">
						Period column order
					</Text>
					<SegmentedControls
						value={periodOrder}
						onChange={setPeriodOrder}
						className="shrink-0"
						aria-label="Period column order"
						data-testid={`${WIP_ANALYSIS_PERIOD_SELECTOR_ID}-order`}
					>
						<SegmentedControls.Segment
							value="newest-first"
							{...periodSelectorIds("order-newest-first")}
						>
							Newest first
						</SegmentedControls.Segment>
						<SegmentedControls.Segment
							value="oldest-first"
							{...periodSelectorIds("order-oldest-first")}
						>
							Oldest first
						</SegmentedControls.Segment>
					</SegmentedControls>
				</div>
			</div>
			<PeriodYearList
				groups={periodYearGroups}
				getItemKey={getItemKey}
				isSelected={isItemSelected}
				onToggle={toggleItem}
				renderLabel={renderLabel}
				testId={WIP_ANALYSIS_PERIOD_SELECTOR_ID}
				trackingId={WIP_ANALYSIS_PERIOD_SELECTOR_ID}
				emptyLabel="No periods available"
				className="flex-1 overflow-y-auto p-3"
			/>
		</div>
	);
};
